import {
  adminOrigin,
  publicOrigin,
  secureOrigin
} from '@maxpreps/network/src/origins';
import { AppType } from '@maxpreps/shared/functions/utils/requestContextUtils';
import { IPageTest } from '../server/IPageTest';
import getNextConfig from './../../constants/getNextConfig';

const { subdomain } = getNextConfig().publicRuntimeConfig;

export const safeEncodeUri = uri => {
  // https://stackoverflow.com/questions/23097928/node-js-throws-btoa-is-not-defined-error
  if (typeof window !== 'undefined') {
    return btoa(uri).replace('/', '_').replace('+', '-');
  }

  return Buffer.from(uri).toString('base64');
};

const getOrigin = () => {
  // to make local testing easier
  if (subdomain === 'local') {
    return 'https://z.maxpreps.com:3000';
  }

  // to make branch testing easier
  if ((subdomain?.indexOf('branch') ?? -1) > -1) {
    return `https://${subdomain}.fe.maxpreps.com`;
  }

  return publicOrigin;
};

const formatUrl = (url, queryParams) => {
  if (queryParams) {
    // eslint-disable-next-line no-restricted-syntax
    for (const [param, value] of Object.entries(queryParams)) {
      url.searchParams.set(param, value);
    }
  }

  return safeEncodeUri(url.toString());
};

export const getReturnUrl = (
  queryParams: object | undefined = undefined,
  url: string | undefined = undefined
) => {
  let calculatedUrl;

  if (url) {
    calculatedUrl = new URL(url, getOrigin());
  } else if (typeof window !== 'undefined') {
    calculatedUrl = new URL(
      window.location.pathname + window.location.search,
      getOrigin()
    );
  } else {
    // we screwed - shouldn't be able to get here but if we do this is a total failure
    throw new Error("Can't generate a returnUrl with the provided variables");
  }

  return formatUrl(calculatedUrl, queryParams);
};

export const replaceOriginKeys = url => {
  if (!url) return '';
  return url
    .replace('[adminServerOrigin]', adminOrigin)
    .replace('[secureServerOrigin]', secureOrigin)
    .replace('[reactUrl]', publicOrigin)
    .replace('[reactServerOrigin]', publicOrigin)
    .replace('[publicServerOrigin]', publicOrigin);
};

export const addGlobalQueryParams = (url, appType) => {
  // Check to see if we need to append apptype to href
  if (
    url &&
    url.toLowerCase().indexOf('apptype=') === -1 &&
    appType != null &&
    appType !== AppType.None
  ) {
    const index = url.indexOf('#') === -1 ? url.length : url.indexOf('#');
    const qsParam =
      url.indexOf('?') === -1 ? `?apptype=${appType}` : `&apptype=${appType}`;

    return url.slice(0, index) + qsParam + url.slice(index, url.length);
  }

  return url;
};

interface INewWindowOptions {
  location: 0 | 1;
  status: 0 | 1;
  scrollbars: 0 | 1;
  resizeable: 0 | 1;
  width: number;
  height: number;
}

const DEFAULT_NEW_WINDOW_OPTIONS: INewWindowOptions = {
  location: 0,
  status: 0,
  scrollbars: 1,
  resizeable: 1,
  width: 750,
  height: 750
};

export const openInNewWindow = (
  event: React.UIEvent<HTMLElement>,
  href: string,
  name: string | undefined = undefined,
  options: INewWindowOptions | undefined = undefined
) => {
  event.preventDefault();
  const config = { ...DEFAULT_NEW_WINDOW_OPTIONS, ...options };
  window.open(href, name, Object.values(config).join(','));
};

export const isPage = (url: string, page: IPageTest) =>
  page.test(new URL(url, 'https://www.maxpreps.com'));
